import { useCallback, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createStore, useStore } from '../../lib/store';
import { Button } from './Button';
import { Dialog, type DialogProps } from './Dialog';
import { createHostRegistry } from './hostRegistry';
import { whenOverlayHistoryIdle } from './overlayHistory';
import { TextField } from './TextField';

export interface DialogController<T> {
  open: boolean;
  /** Closes the dialog; the promise resolves with `value` after the exit fade. */
  close: (value: T) => void;
  /** Pass to <Dialog onClosed>. */
  onClosed: () => void;
}

interface Entry {
  id: number;
  render: (controller: DialogController<unknown>) => ReactNode;
  resolve: (value: unknown) => void;
}

const dialogStore = createStore<Entry[]>([]);
let sequence = 0;

const registry = createHostRegistry('dialogs', () => DialogHost);

/**
 * Shows a custom dialog imperatively. `render` receives the controller and
 * returns a <Dialog>. Resolves once the dialog has fully closed (its history
 * entry is gone, so callers may navigate immediately).
 */
export function showDialog<T>(render: (controller: DialogController<T>) => ReactNode): Promise<T> {
  registry.ensureMounted();
  return new Promise<T>((resolve) => {
    const entry: Entry = {
      id: ++sequence,
      render: render as Entry['render'],
      resolve: resolve as Entry['resolve'],
    };
    dialogStore.set((list) => [...list, entry]);
  });
}

export interface ConfirmDialogOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm action. */
  destructive?: boolean;
  /** FilledButton (default, e.g. "Delete" / "Reset") or TextButton (bookmark delete). */
  confirmVariant?: 'filled' | 'text';
  titleStyle?: DialogProps['titleStyle'];
  variant?: DialogProps['variant'];
}

/** AlertDialog with Cancel + confirm; resolves true only when confirmed. */
export function confirmDialog({
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  destructive = false,
  confirmVariant = 'filled',
  titleStyle,
  variant,
}: ConfirmDialogOptions): Promise<boolean> {
  return showDialog<boolean>(({ open, close, onClosed }) => (
    <Dialog
      open={open}
      onClose={() => close(false)}
      onClosed={onClosed}
      alert
      title={title}
      titleStyle={titleStyle}
      variant={variant}
      actions={[
        { label: cancelLabel, onClick: () => close(false) },
        {
          label: confirmLabel,
          onClick: () => close(true),
          tone: destructive ? 'destructive' : 'primary',
          variant: confirmVariant,
          isDefault: !destructive,
        },
      ]}
    >
      {message}
    </Dialog>
  ));
}

export interface PromptDialogOptions {
  title: string;
  label?: string;
  hint?: string;
  initialValue?: string;
  suffixText?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'url' | 'email';
  confirmLabel?: string;
  cancelLabel?: string;
  /** Enables the confirm button only for valid input. */
  validate?: (value: string) => boolean;
  titleStyle?: DialogProps['titleStyle'];
}

function PromptDialog({ options, controller }: { options: PromptDialogOptions; controller: DialogController<string | null> }) {
  const [value, setValue] = useState(options.initialValue ?? '');
  // Focused through the dialog's focus trap rather than `autoFocus`, so the
  // trap still sees (and later restores) the control that opened the dialog.
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const valid = options.validate ? options.validate(value) : true;
  const submit = () => {
    if (valid) controller.close(value);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    // Closing returns focus to the control that opened the dialog within this keydown; without
    // preventDefault the Enter's keypress then lands on that button and opens the dialog again.
    event.preventDefault();
    submit();
  };
  return (
    <Dialog
      open={controller.open}
      onClose={() => controller.close(null)}
      onClosed={controller.onClosed}
      title={options.title}
      titleStyle={options.titleStyle}
      initialFocus={inputRef}
      actions={
        <>
          <Button variant="text" onClick={() => controller.close(null)}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button variant="filled" onClick={submit} disabled={!valid}>
            {options.confirmLabel ?? 'Save'}
          </Button>
        </>
      }
    >
      <TextField
        inputRef={inputRef}
        label={options.label}
        hint={options.hint}
        suffixText={options.suffixText}
        inputMode={options.inputMode}
        value={value}
        onValueChange={setValue}
        onKeyDown={onKeyDown}
      />
    </Dialog>
  );
}

/** Single-field input dialog (e.g. "Custom Timer" minutes); resolves the text or null when cancelled. */
export function promptDialog(options: PromptDialogOptions): Promise<string | null> {
  return showDialog<string | null>((controller) => <PromptDialog options={options} controller={controller} />);
}

function HostEntry({ entry }: { entry: Entry }) {
  const [open, setOpen] = useState(true);
  const result = useRef<unknown>(undefined);
  const settled = useRef(false);
  const close = useCallback((value: unknown) => {
    if (settled.current) return;
    settled.current = true;
    result.current = value;
    setOpen(false);
  }, []);
  const onClosed = useCallback(() => {
    dialogStore.set((list) => list.filter((e) => e !== entry));
    // The dialog's history entry is removed with an asynchronous history.back();
    // resolving only after it lands lets callers navigate immediately.
    void whenOverlayHistoryIdle().then(() => entry.resolve(result.current));
  }, [entry]);
  return <>{entry.render({ open, close, onClosed })}</>;
}

/** Renders imperative dialogs. Mount once in the app shell. */
export function DialogHost() {
  const primary = registry.usePrimaryHost();
  const entries = useStore(dialogStore);
  if (!primary) return null;
  return (
    <>
      {entries.map((entry) => (
        <HostEntry key={entry.id} entry={entry} />
      ))}
    </>
  );
}
